// ---------------------------------------------------------------------------
// Comparison Dimensions Registry
// ---------------------------------------------------------------------------
// Configuration for all 12 supported comparison dimension types
// Single source of truth for dimension metadata
// ---------------------------------------------------------------------------

import type { DimensionTypeConfig } from '@/types/comparison'

export const COMPARISON_DIMENSIONS: readonly DimensionTypeConfig[] = [
  // Saved Segments
  {
    type: 'persona',
    label: 'Persona',
    icon: 'Users',
    searchable: false,
    placeholder: 'Select a saved persona',
    description: 'Compare saved audience segments',
  },

  // Entities
  {
    type: 'brand',
    label: 'Brand',
    icon: 'Tag',
    searchable: true,
    placeholder: 'Search brands...',
    description: 'Compare brand followership',
  },
  {
    type: 'team',
    label: 'Team',
    icon: 'Trophy',
    searchable: true,
    placeholder: 'Search teams...',
    description: 'Compare sports team fanbases',
  },
  {
    type: 'author',
    label: 'Author',
    icon: 'User',
    searchable: true,
    placeholder: 'Search by username...',
    description: 'Compare individual profile audiences',
  },
  {
    type: 'channel',
    label: 'Channel',
    icon: 'Tv',
    searchable: true,
    placeholder: 'Search channels...',
    description: 'Compare content channel audiences',
  },

  // Content
  {
    type: 'post',
    label: 'Post',
    icon: 'FileText',
    searchable: true,
    placeholder: 'Search posts...',
    description: 'Compare specific post engagement',
  },
  {
    type: 'hashtag',
    label: 'Hashtag',
    icon: 'Hash',
    searchable: true,
    placeholder: 'Search hashtags...',
    description: 'Compare hashtag reach',
  },
  {
    type: 'category',
    label: 'Category',
    icon: 'Layers',
    searchable: false,
    placeholder: 'Select category',
    description: 'Compare content categories',
  },

  // Demographics
  {
    type: 'platform',
    label: 'Platform',
    icon: 'Globe',
    searchable: false,
    placeholder: 'Select platform',
    description: 'Compare social platforms',
  },
  {
    type: 'country',
    label: 'Country',
    icon: 'MapPin',
    searchable: false,
    placeholder: 'Select country...',
    description: 'Compare geographic markets',
  },
  {
    type: 'ageGroup',
    label: 'Age Group',
    icon: 'Calendar',
    searchable: false,
    placeholder: 'Select age group',
    description: 'Compare generational cohorts',
  },
  {
    type: 'emotion',
    label: 'Emotion',
    icon: 'Heart',
    searchable: false,
    placeholder: 'Select emotion',
    description: 'Compare emotional sentiment profiles',
  },
] as const

// Helper to get dimension config by type
export function getDimensionConfig(type: string): DimensionTypeConfig | undefined {
  return COMPARISON_DIMENSIONS.find((d) => d.type === type)
}

// Grouped dimensions for UI rendering
export const DIMENSION_GROUPS = {
  saved: COMPARISON_DIMENSIONS.filter((d) => d.type === 'persona'),
  entities: COMPARISON_DIMENSIONS.filter((d) =>
    ['brand', 'team', 'author', 'channel'].includes(d.type)
  ),
  content: COMPARISON_DIMENSIONS.filter((d) =>
    ['post', 'hashtag', 'category'].includes(d.type)
  ),
  demographics: COMPARISON_DIMENSIONS.filter((d) =>
    ['platform', 'country', 'ageGroup', 'emotion'].includes(d.type)
  ),
} as const
